/**
 * Erros da API — sempre com mensagem legível em português.
 *
 * Requisitos que este arquivo atende:
 *  - nunca exibir JSON cru nem stacktrace ao usuário;
 *  - nunca deixar acontecer o erro "Unexpected token '<'" (resposta HTML do nginx
 *    interpretada como JSON) — é detectado e traduzido;
 *  - 401 do app desloga; 401 do SISCOMEX NÃO desloga, só pede reconexão;
 *  - respeitar o bloqueio de ~60 s de reautenticação do SISCOMEX com contagem;
 *  - tratar 403 e offline, que hoje não são tratados em lugar nenhum (B-24).
 */

export type ApiErrorKind =
  | 'rede' // sem conexão / fetch falhou
  | 'offline' // service worker respondeu offline
  | 'timeout' // estourou o tempo (inclui o corte de 100 s do Cloudflare)
  | 'sessao_expirada' // 401 do app  → deslogar
  | 'siscomex_desconectado' // 401 do SISCOMEX → só reconectar
  | 'siscomex_bloqueado' // reautenticação bloqueada por ~60 s
  | 'siscomex_erro' // erro de negócio do Portal Único (tem código próprio)
  | 'operacao_falhou' // 200 com success:false sem origem no Portal Único
  | 'sem_permissao' // 403
  | 'nao_encontrado' // 404
  | 'validacao' // 400/422
  | 'servidor' // 5xx
  | 'resposta_invalida' // veio HTML ou JSON quebrado
  | 'desconhecido'

/**
 * Traduz erro de login. Credencial errada e servidor indisponível são
 * problemas diferentes, e o usuário age diferente em cada caso.
 */
export function mensagemDeLogin(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 401) return 'Usuário ou senha incorretos.'
    if (e.kind === 'offline') return 'Sem conexão. Verifique sua internet e tente de novo.'
    if (e.kind === 'servidor') return 'O sistema está indisponível no momento. Tente em instantes.'
    return e.message
  }
  return 'Não foi possível entrar. Tente de novo.'
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind
  readonly status: number | null
  /** Código de erro do SISCOMEX, ex.: "DIMP-ER8014". */
  readonly siscomexCode: string | null
  /** Segundos a aguardar (usado em `siscomex_bloqueado`). */
  readonly retryAfter: number | null
  /** Corpo original, para o painel de detalhes técnicos (nunca exibido cru). */
  readonly detail: unknown

  constructor(
    kind: ApiErrorKind,
    message: string,
    opts: {
      status?: number | null
      siscomexCode?: string | null
      retryAfter?: number | null
      detail?: unknown
    } = {},
  ) {
    super(message)
    this.name = 'ApiError'
    this.kind = kind
    this.status = opts.status ?? null
    this.siscomexCode = opts.siscomexCode ?? null
    this.retryAfter = opts.retryAfter ?? null
    this.detail = opts.detail
  }

  /** Vale a pena oferecer "Tentar de novo"? */
  get recuperavel(): boolean {
    return (
      this.kind === 'rede' ||
      this.kind === 'offline' ||
      this.kind === 'timeout' ||
      this.kind === 'servidor' ||
      this.kind === 'siscomex_bloqueado' ||
      this.kind === 'resposta_invalida' ||
      this.kind === 'operacao_falhou'
    )
  }

  /** Deve derrubar a sessão do app? Só o 401 real do app. */
  get deveDeslogar(): boolean {
    return this.kind === 'sessao_expirada'
  }
}

/* -------------------------------------------------------------- mensagens */

const MENSAGENS: Record<ApiErrorKind, string> = {
  rede: 'Não foi possível falar com o servidor. Verifique sua conexão.',
  offline: 'Você está sem conexão. Reconecte para continuar.',
  timeout:
    'A operação demorou mais do que o permitido e foi interrompida. Tente novamente.',
  sessao_expirada: 'Sua sessão expirou. Entre novamente.',
  siscomex_desconectado:
    'Sem conexão com o SISCOMEX. Reconecte em Configurações para continuar.',
  siscomex_bloqueado:
    'O SISCOMEX bloqueia uma nova autenticação por alguns segundos. Aguarde e tente de novo.',
  siscomex_erro: 'O SISCOMEX recusou a operação.',
  operacao_falhou: 'Não foi possível concluir a operação. Tente novamente.',
  sem_permissao: 'Você não tem permissão para esta ação.',
  nao_encontrado: 'Não encontramos o que você procura.',
  validacao: 'Confira os dados informados.',
  servidor: 'O servidor encontrou um erro. Tente novamente em instantes.',
  resposta_invalida:
    'O servidor devolveu uma resposta inesperada. Tente novamente; se persistir, avise o suporte.',
  desconhecido: 'Algo deu errado. Tente novamente.',
}

export const mensagemPadrao = (kind: ApiErrorKind): string => MENSAGENS[kind]

/* ----------------------------------------------------- detecção de padrões */

/**
 * 401 do app vs 401 do SISCOMEX.
 * Regra preservada do sistema atual (index.html): só estas mensagens indicam
 * sessão do app expirada. Qualquer outro 401 é do Portal Único e NÃO desloga.
 */
const PADRAO_SESSAO_APP =
  /token n[aã]o fornecido|token inv[aá]lido ou expirado|usu[aá]rio n[aã]o encontrado|could not validate credentials/i

export const ehSessaoDoApp = (texto: string): boolean => PADRAO_SESSAO_APP.test(texto)

/** "Token reuse required" / PLAT-ER2033 = bloqueio de ~60 s do Portal Único. */
const PADRAO_BLOQUEIO = /token reuse required|PLAT-ER2033/i

export const ehBloqueioSiscomex = (texto: string): boolean => PADRAO_BLOQUEIO.test(texto)

/** Extrai códigos do Portal Único, ex.: DIMP-ER8014, PLAT-ER2033, CATP-ER0001. */
export function extrairCodigoSiscomex(texto: string): string | null {
  const m = /\b([A-Z]{3,6}-ER\d{3,4})\b/.exec(texto)
  return m ? m[1] : null
}

/** A resposta é HTML? (página de erro do nginx/Cloudflare devolvida como JSON) */
export const ehHtml = (texto: string): boolean =>
  /^\s*(<!doctype|<html|<head|<body)/i.test(texto)

/**
 * Extrai a mensagem de erro de um corpo do FastAPI.
 * `detail` pode ser string, objeto, ou lista de erros de validação do Pydantic.
 */
export function extrairDetalhe(corpo: unknown): string | null {
  if (!corpo || typeof corpo !== 'object') return null
  const d = (corpo as Record<string, unknown>).detail ?? (corpo as Record<string, unknown>).error
  if (typeof d === 'string') return d
  if (Array.isArray(d)) {
    const msgs = d
      .map((x) => (x && typeof x === 'object' ? String((x as Record<string, unknown>).msg ?? '') : ''))
      .filter(Boolean)
    return msgs.length ? msgs.join('; ') : null
  }
  if (d && typeof d === 'object') {
    const msg = (d as Record<string, unknown>).msg ?? (d as Record<string, unknown>).message
    if (typeof msg === 'string') return msg
  }
  const msg = (corpo as Record<string, unknown>).message
  return typeof msg === 'string' ? msg : null
}
